/**
 * Brand-group runtime guard.
 *
 * Enforces that a customer who installed the PWA from an establishment slug
 * stays within the owner's brand group (sibling units + customer area) while
 * in standalone mode.  The guard is a UX/safety surface — it does NOT replace
 * server-side authorization, which is unchanged.
 *
 * The guard is a strict no-op when the app is running in a regular browser
 * tab (non-standalone), and also when the device is an administrator device
 * (see lib/installProfile.ts).  It never blocks navigation in those contexts.
 *
 * ADR-002, ADR-003.
 */

import { getPublicSiblingEstablishments } from "../services/public";
import {
  ADMIN_ESCAPE_PARAM,
  ADMIN_ESCAPE_VALUE,
  BRAND_GROUP_CACHE_PREFIX,
  getInstallingSlug,
  isAdminDevice,
  setInstallProfile,
  setInstallingSlug,
} from "./installProfile";

// Reexportados para nao quebrar quem ja importava estes helpers daqui.
export { getInstallingSlug, setInstallingSlug };

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Cache key is per-slug to avoid stale sibling data when different slugs are visited in the same session. */
const brandGroupCacheKey = (slug: string) => `${BRAND_GROUP_CACHE_PREFIX}${slug}`;

/**
 * Route prefixes that are always blocked when the app is in customer-installed
 * standalone mode. Paths must be lowercased for comparison.
 */
const BLOCKED_PREFIXES = ["/admin", "/login", "/cadastro-estabelecimento"] as const;

// ---------------------------------------------------------------------------
// Standalone detection (mirrors PwaRegistration.tsx isStandalone)
// ---------------------------------------------------------------------------

export function isStandaloneMode(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.matchMedia("(display-mode: window-controls-overlay)").matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

// ---------------------------------------------------------------------------
// Installing slug persistence
// ---------------------------------------------------------------------------

/**
 * Registra o slug de instalacao para uma instalacao de cliente.
 *
 * Substitui as chamadas diretas a `setInstallingSlug` nas paginas publicas e na
 * area do cliente. A diferenca importante: um dispositivo de administrador
 * nunca e travado, mesmo que o administrador navegue por uma pagina de marca
 * ou pela area do cliente dentro do proprio app instalado.
 *
 * @param slug - slug da marca que esta sendo aberta.
 * @param options.force - grava mesmo fora do modo standalone e mesmo que ja
 *   exista um slug gravado. Usado no evento `appinstalled`, quando a instalacao
 *   acabou de acontecer a partir desta marca.
 */
export function recordCustomerInstallSlug(
  slug: string,
  options: { force?: boolean } = {}
): void {
  const { force = false } = options;

  if (typeof window === "undefined") return;
  if (!slug) return;

  // Dispositivo de admin: nunca trava.
  if (isAdminDevice()) return;

  if (!force) {
    if (!isStandaloneMode()) return;
    if (getInstallingSlug()) return;
  }

  setInstallingSlug(slug);
  setInstallProfile("customer");
}

// ---------------------------------------------------------------------------
// Brand group resolution
// ---------------------------------------------------------------------------

/**
 * Resolves the full set of slugs that belong to the same owner as
 * `installingSlug`, including the installing slug itself.
 *
 * Results are cached in sessionStorage for the lifetime of the browser
 * session to avoid a redundant network round-trip on every navigation.
 */
export async function resolveBrandGroupSlugs(
  installingSlug: string
): Promise<Set<string>> {
  if (typeof window === "undefined") return new Set([installingSlug]);

  // Attempt to read from session cache (keyed by slug to avoid stale data between brands).
  const cacheKey = brandGroupCacheKey(installingSlug);
  try {
    const cached = window.sessionStorage.getItem(cacheKey);
    if (cached) {
      const parsed: unknown = JSON.parse(cached);
      if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === "string") {
        return new Set(parsed as string[]);
      }
    }
  } catch {
    // Ignore cache read errors and fall through to a fresh fetch.
  }

  // Fetch sibling establishments from the public API.
  let slugs: string[] = [installingSlug];
  try {
    const siblings = await getPublicSiblingEstablishments(installingSlug);
    const siblingSlugSet = siblings.map((b) => b.slug).filter(Boolean);
    slugs = Array.from(new Set([installingSlug, ...siblingSlugSet]));
  } catch {
    // Network error — fall back to allowing only the installing slug.
    slugs = [installingSlug];
  }

  // Write to session cache.
  try {
    window.sessionStorage.setItem(cacheKey, JSON.stringify(slugs));
  } catch {
    // Ignore write errors.
  }

  return new Set(slugs);
}

// ---------------------------------------------------------------------------
// Navigation check
// ---------------------------------------------------------------------------

/**
 * Extracts the first non-empty path segment from a URL pathname.
 * e.g. "/barbearia-x/servicos" → "barbearia-x"
 *      "/cliente/area"         → "cliente"
 *      "/admin"                → "admin"
 */
function firstSegment(pathname: string): string {
  const parts = pathname.replace(/^\/+/, "").split("/");
  return parts[0]?.toLowerCase() ?? "";
}

/** Separa pathname, query e hash de um alvo de navegacao. */
function splitTarget(targetPath: string): { pathname: string; search: string } {
  const withoutHash = (targetPath || "").split("#")[0] ?? "";
  const [pathname = "", search = ""] = withoutHash.split("?");
  return { pathname, search };
}

/**
 * Saida de emergencia: `/login?acesso=estabelecimento`.
 *
 * Sempre liberada, inclusive num app travado, para que um administrador
 * consiga voltar ao login do estabelecimento sem apagar os dados do site nem
 * reinstalar o app.
 */
function isAdminEscape(pathname: string, search: string): boolean {
  const normalized = pathname.toLowerCase().replace(/\/+$/, "");
  if (normalized !== "/login") return false;

  try {
    return new URLSearchParams(search).get(ADMIN_ESCAPE_PARAM) === ADMIN_ESCAPE_VALUE;
  } catch {
    return false;
  }
}

/**
 * Synchronous check against the brand group and blocked-prefix rules.
 *
 * This helper is separated so it can be tested without a DOM.
 */
export function checkNavigationSync(
  targetPath: string,
  allowedSlugs: Set<string>
): "allow" | "redirect" {
  const { pathname, search } = splitTarget(targetPath);
  const lower = pathname.toLowerCase();

  // Always allow the root path.
  if (lower === "/" || lower === "") return "allow";

  // Saida de emergencia para o login do estabelecimento — precisa ser avaliada
  // ANTES da lista de rotas bloqueadas, que inclui "/login".
  if (isAdminEscape(pathname, search)) return "allow";

  // Block admin/login routes unconditionally.
  for (const prefix of BLOCKED_PREFIXES) {
    if (lower === prefix || lower.startsWith(`${prefix}/`)) {
      return "redirect";
    }
  }

  const segment = firstSegment(lower);

  // Customer area is always allowed.
  if (segment === "cliente") return "allow";

  // Checkout and agendamento are neutral public flows — allow.
  if (segment === "checkout" || segment === "agendamento") return "allow";

  // If the first segment is a known brand slug → allow.
  if (allowedSlugs.has(segment)) return "allow";

  // If the first segment looks like a slug (non-empty, no system route) and it
  // is NOT in the brand group → block.  We only block when the segment is
  // non-empty and does not match any known system route we haven't already
  // handled.  Empty string falls through to allow (root).
  if (segment.length > 0) return "redirect";

  return "allow";
}

/**
 * Async navigation check — the public API for the guard.
 *
 * @param targetPath - The pathname + optional search/hash to navigate to
 *   (e.g. `window.location.pathname + window.location.search`).
 * @param installingSlug - The slug from which the PWA was installed.
 * @returns `"allow"` to proceed, `"redirect"` to bounce back to
 *   `/{installingSlug}`.
 */
export async function checkNavigation(
  targetPath: string,
  installingSlug: string
): Promise<"allow" | "redirect"> {
  // Guard is no-op outside of standalone mode.
  if (!isStandaloneMode()) return "allow";

  // Dispositivo de administrador nunca e bloqueado, mesmo que exista um slug
  // de instalacao gravado por uma visita anterior a area do cliente.
  if (isAdminDevice()) return "allow";

  const allowedSlugs = await resolveBrandGroupSlugs(installingSlug);
  return checkNavigationSync(targetPath, allowedSlugs);
}
